// Global
import {
  Image as JSSImage,
  ImageField,
  LayoutServicePageState,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { getAbsolutePath } from 'lib/util/urls';
import NextImage from 'next/image';

/**
 * JSS does not yet support Next Image in Exprience Editor
 * This component will switch between the two based on environment
 * which allows us to get the various performance benefits from Next Image
 *
 * Note that the images may display slightly differently in
 * Experience Editor as the JSS Image component doesn't have the same layout options
 */

interface SizedImageField extends ImageField {
  value?: {
    alt?: string;
    height?: string;
    src?: string;
    width?: string;
  };
}

type NextImageLayoutOption = 'intrinsic' | 'responsive' | 'fill';

interface AnyNextImage {
  alt: string;
  height?: string;
  layout: NextImageLayoutOption;
  objectFit?: 'cover';
  priority?: boolean;
  src: string;
  width?: string;
  className?: string;
  editable?: boolean;
  unoptimized?: boolean;
}

interface ImageWrapperProps {
  field: SizedImageField;
  layout?: NextImageLayoutOption;
  priority?: boolean;
  className?: string;
  editable?: boolean;
}

const publicUrl = new URL(process.env.PUBLIC_URL as string);

const ImageWrapper = ({
  field,
  layout = 'intrinsic',
  priority,
  className,
  editable,
}: ImageWrapperProps): JSX.Element => {
  const sitecoreContext = useSitecoreContext();
  // Checking if we're in sitecore, even if we are not in experience editor
  // This supports EE and Preview
  const isInSitecore = sitecoreContext.sitecoreContext.pageState !== LayoutServicePageState.Normal;
  if (isInSitecore) {
    return <JSSImage field={field} className={className} />;
  }
  if (!field.value || !field.value.src) {
    return <></>;
  }
  // Make a copy of the field so we don't mess with any values
  const nextImageProps: AnyNextImage = {
    src: String(getAbsolutePath(field.value.src)),
    alt: field.value?.alt ?? '',
    unoptimized: /\.svg(\?.*)$/g.test(field.value.src),
    layout,
    priority,
    className,
    editable,
  };
  // Adding a check to see if it's a local image, and if so we want to remove the domain
  // from the URL so it can work with Next Image
  // This comes up when we have images from Algolia which will be fully qualified
  // but should actually have be treated as an internal url
  let isLocalImage = false;
  if (field.value.src) {
    const originalSrc = field.value.src;
    // If it's a fully qualified url, use it as is, otherwise include the public url
    const imageUrl = originalSrc.startsWith('http')
      ? new URL(originalSrc)
      : new URL(originalSrc, publicUrl);

    // If we're on localhost, treat external images as local.  Should work most of the time.
    // This is to handle images locally from Algolia since there is no local index
    isLocalImage =
      publicUrl.origin === 'http://localhost:3000' || publicUrl.origin === imageUrl.origin;
    if (isLocalImage) {
      nextImageProps.src = imageUrl.pathname + imageUrl.search;
    }
  }
  // Making an exception for Storybook so we can test NextImage properly
  if (!isLocalImage && !process.env.IS_STORYBOOK) {
    // Disabling warning, this is a fallback, since external images will fail unless they're whitelisted
    // eslint-disable-next-line @next/next/no-img-element
    return <img src={nextImageProps.src} alt={nextImageProps.alt} className={className} />;
  }

  if (layout === 'fill') {
    nextImageProps.objectFit = 'cover';
  } else {
    nextImageProps.height = field.value.height;
    nextImageProps.width = field.value.width;
  }
  if (!nextImageProps.height && !nextImageProps.width && layout !== 'fill') {
    nextImageProps.layout = 'fill';
  }

  return <NextImage {...nextImageProps} />;
};

export default ImageWrapper;
